<script>
    import { preventDefault } from 'svelte/legacy';
    import { onMount } from 'svelte';
    import { 
        environment 
    } from "@app/store/envStore.svelte.js";
    import { toast } from '@zerodevx/svelte-toast';
    import { fade } from 'svelte/transition';
    import * as m from "@app/paraglide/messages.js";

    let backupDestination = $state(environment.online ? "Cloud" : "Local"); // Cloud or Local
    let targetFolder = $state(""); // Target Folder for Local Backup
    let numberOfBackups = $state(5); // Number of backups to keep
    let backupInterval = $state(15); // Backup Interval in minutes
    let enableAutomaticSave = $state(false); // Enable Automatic Save
    let automaticSaveInterval = $state(5); // Automatic Save Interval in minutes

    onMount(() => {
        // Load settings from local storage
        let settings = JSON.parse(localStorage.getItem("cc-automatic-backup-settings"));
        if(settings){
            backupDestination = environment.online ? settings.backupDestination : "Local";
            targetFolder = settings.targetFolder || "";
            numberOfBackups = Math.min(5, settings.numberOfBackups);
            backupInterval = Math.max(15, settings.backupInterval);
            enableAutomaticSave = settings.enableAutomaticSave || false;
            automaticSaveInterval = Math.max(5, settings.automaticSaveInterval);
        }
    });

    // Save Changes to Local Storage
    function saveChanges(){
        // Save changes to local storage
        localStorage.setItem("cc-automatic-backup-settings", JSON.stringify({
            backupDestination: backupDestination,
            targetFolder : targetFolder,
            numberOfBackups : numberOfBackups,
            backupInterval : backupInterval,
            enableAutomaticSave : enableAutomaticSave,
            automaticSaveInterval : Math.max(5, automaticSaveInterval)
        }));

        toast.push(m.options_backup_restart_notice(), {
            classes: ["toast-warning"]
        });
        toast.push(m.options_backup_updated(), {
            classes: ["toast-success"]
        });
    }

    function selectTargetFolder() {
        ipcRenderer.invoke('selectFolder', {
            title: m.options_backup_select_folder_title(),
            properties: ["openDirectory", "createDirectory"]
        }).then((path) => {
            if (!path.canceled) {
                targetFolder = path.filePaths[0];
            }
        });
    }
</script>

<div class="backup-container">
    <div class="backup-card">
        <div class="backup-header">
            <h5>{m.options_backup_auto_save_title()}</h5>
            <p class="text-muted mb-3">{m.options_backup_auto_save_desc()}</p>
        </div>
        <div class="backup-form">
            <form id="automaticBackupForm" onsubmit={preventDefault(saveChanges)} aria-label={m.options_backup_form_label()}>   
                <div class="form-check form-switch mb-3">
                    <input 
                        class="form-check-input me-2" 
                        type="checkbox" 
                        bind:checked={enableAutomaticSave} 
                        id="enableAutomaticSaveSwitch"
                    >
                    <label class="form-check-label" for="enableAutomaticSaveSwitch">
                        {m.options_backup_enable_auto_save()}
                    </label>
                </div>
                
                <div class="mb-3">
                    <label class="form-label d-flex justify-content-between" for="automaticSaveIntervalInput">
                        <span>{m.options_backup_save_interval_label()}</span>
                        <small class="text-muted">{m.options_backup_minimum_5()}</small>
                    </label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            class="form-control" 
                            bind:value={automaticSaveInterval} 
                            min="5" 
                            max="999" 
                            disabled={!enableAutomaticSave}
                            id="automaticSaveIntervalInput"
                        >
                        <span class="input-group-text"><i class="bi bi-clock"></i></span>
                    </div>
                    <div class="form-text">{m.options_backup_save_interval_help()}</div>
                </div>
            </form>
        </div>
    </div>

    <div class="backup-card">
        <div class="backup-header">
            <h5>{m.options_backup_snapshot_title()}</h5>
            <p class="text-muted mb-3">{m.options_backup_snapshot_desc()}</p>
        </div>
        <div class="backup-form">
            <div class="mb-3">
                <label class="form-label" for="backupDestinationSelect">{m.options_backup_destination_label()}</label>
                <select 
                    id="backupDestinationSelect" 
                    class="form-select" 
                    bind:value={backupDestination} 
                    aria-label={m.options_backup_destination_label()}
                >
                    {#if environment.online}
                        <option value="Cloud">{m.options_backup_destination_cloud()}</option>
                    {/if}
                    {#if environment.electron}
                        <option value="Local">{m.options_backup_destination_local()}</option>
                    {/if}
                </select>
            </div>
            
            {#if backupDestination === "Local"}
                <div class="folder-selector border" transition:fade={{ duration: 150 }}>
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <label for="selectTargetFolderButton" class="form-label mb-0">{m.options_backup_target_location()}</label>
                        <button 
                            id="selectTargetFolderButton" 
                            type="button" 
                            class="btn btn-sm btn-outline-primary" 
                            onclick={selectTargetFolder} 
                            aria-label={m.options_backup_select_folder_label()}
                        >
                            <i class="bi bi-folder2-open me-1"></i> {m.options_backup_browse()}
                        </button>
                    </div>
                    <div class="path-display bg-secondary">
                        <p class="text-truncate mb-0 small">
                            {targetFolder || m.options_backup_no_folder_selected()}
                        </p>
                    </div>
                    {#if !targetFolder}
                        <div class="alert alert-warning mt-2 py-2 px-3 small">
                            <i class="bi bi-exclamation-triangle me-1"></i>
                            {m.options_backup_select_target_warning()}
                        </div>
                    {/if}

                    <div class="mb-3 mt-3">
                        <label class="form-label d-flex justify-content-between" for="numberOfBackupsInput">
                            <span>{m.options_backup_number_to_keep_label()}</span>
                            <small class="text-muted">{m.options_backup_maximum_10()}</small>
                        </label>
                        <div class="input-group">
                            <input 
                                type="number" 
                                class="form-control" 
                                bind:value={numberOfBackups} 
                                min="1" 
                                max="10"
                                id="numberOfBackupsInput"
                            >
                            <span class="input-group-text"><i class="bi bi-layers"></i></span>
                        </div>
                        <div class="form-text">{m.options_backup_number_to_keep_help()}</div>
                    </div>
                </div>
            {/if}
            
            <div class="mb-3">
                <label class="form-label d-flex justify-content-between" for="backupIntervalInput">
                    <span>{m.options_backup_interval_label()}</span>
                    <small class="text-muted">{m.options_backup_minimum_10()}</small>
                </label>
                <div class="input-group">
                    <input 
                        type="number" 
                        class="form-control" 
                        bind:value={backupInterval} 
                        min="10" 
                        max="999"
                        id="backupIntervalInput"
                    >
                    <span class="input-group-text"><i class="bi bi-clock-history"></i></span>
                </div>
                <div class="form-text">{m.options_backup_interval_help()}</div>
            </div>
            
            <div class="d-flex justify-content-end mt-4">
                <button 
                    id="saveBackupSettingsButton" 
                    class="btn btn-primary" 
                    type="button" 
                    onclick={saveChanges} 
                    aria-label={m.options_backup_save_settings_label()}
                >
                    <i class="bi bi-check-lg me-1"></i> {m.options_backup_save_settings()}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
    .backup-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .backup-card {
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .backup-header {
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.1);
    }

    .backup-header h5 {
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .backup-form {
        padding: 1rem;
    }

    .folder-selector {
        background-color: rgba(0,0,0,0.02);
        padding: 1rem;
        border-radius: 0.4rem;
        margin-bottom: 1rem;
    }

    .path-display {
        background-color: white;
        border: 1px solid rgba(0,0,0,0.1);
        border-radius: 0.25rem;
        padding: 0.5rem 0.75rem;
        min-height: 38px;
        display: flex;
        align-items: center;
    }

    .form-check-input {
        width: 2.5rem;
        height: 1.25rem;
    }

    .form-check-input:checked {
        background-color: var(--bs-primary);
        border-color: var(--bs-primary);
    }

    .input-group-text {
        background-color: var(--bs-light);
    }

    .form-text {
        font-size: 0.85rem;
        color: var(--bs-secondary);
    }

    input:disabled {
        background-color: var(--bs-light);
        opacity: 0.75;
    }
</style>